import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Page, PageErrorState, Typography, useOverlayContext, FunctionalLoader } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { useIntegrations } from '~/hooks/reactQuery/queries/useIntegrations';
import { useEmailAuthCompletion } from '~/hooks/useEmailAuthCompletion';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useIntegration } from '~/hooks/useIntegration';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { integrationModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { Accounting } from '~/types/accounting';
import { relativePath } from '~/utils/routePaths';
import { AccountingSetupPage } from '../Accounting/AccountingSetupPage/AccountingSetupPage';
import { ClientHubSetupPage } from '../PracticeManagement/ClientHubSetupPage';
import { FinancialCentsSetupPage } from '../PracticeManagement/FinancialCentsSetupPage';
import { KarbonSetupPage } from '../PracticeManagement/KarbonSetupPage';
import { KeeperSetupPage } from '../PracticeManagement/KeeperSetupPage';
import { AbacorCard } from './AbacorCard/AbacorCard';
import styles from './AvailableAppsPage.module.scss';
import ClientHubCard from './ClientHubCard/ClientHubCard';
import CountCard from './CountCard/CountCard';
import FinancialCentsCard from './FinancialCentsCard/FinancialCentsCard';
import GmailCard from './GmailCard/GmailCard';
import HubspotViaZapierCard from './HubspotViaZapierCard/HubspotViaZapierCard';
import HubspotViaZapierPage from './HubspotViaZapierPage/HubspotViaZapierPage';
import KarbonCard from './KarbonCard/KarbonCard';
import KeeperCard from './Keeper/KeeperCard/KeeperCard';
import LevvyCard from './LevvyCard/LevvyCard';
import MakeCard from './MakeCard/MakeCard';
import MondayCard from './MondayCard/MondayCard';
import OutlookCard from './OutlookCard/OutlookCard';
import PingCard from './PingCard/PingCard';
import QboCard from './QboCard/QboCard';
import SlackViaZapierCard from './SlackViaZapierCard/SlackViaZapierCard';
import SlackViaZapierPage from './SlackViaZapierPage/SlackViaZapierPage';
import { StanfordTaxCard } from './StanfordTax/StanfordTaxCard/StanfordTaxCard';
import { StanfordTaxSetupPage } from './StanfordTax/StanfordTaxSetupPage/StanfordTaxSetupPage';
import XeroCard from './XeroCard/XeroCard';
import ZapierCard from './ZapierCard/ZapierCard';
import { ZapierTemplateIntegrationCards } from './ZapierTemplates/ZapierTemplateIntegrationCards';
import { ZapierTemplatesPage } from './ZapierTemplates/ZapierTemplatesPage';
export var AvailableAppsSection = /*#__PURE__*/ function(AvailableAppsSection) {
    AvailableAppsSection["AccountingSoftware"] = "accountingSoftware";
    AvailableAppsSection["Communications"] = "communications";
    AvailableAppsSection["WorkflowAutomation"] = "workflowAutomation";
    AvailableAppsSection["PracticeManagement"] = "practiceManagement";
    AvailableAppsSection["Crm"] = "crm";
    AvailableAppsSection["TaxWorkflow"] = "taxWorkflow";
    AvailableAppsSection["Forms"] = "forms";
    AvailableAppsSection["Scheduling"] = "scheduling";
    return AvailableAppsSection;
}({});
export const GetAvailableAppsPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            AvailableAppsPageTID: {
                ErrorState: 'integrations-error-state',
                Page: 'integrations-page'
            }
        }
    });
const IntegrationSection = (param)=>{
    let { title, testId, children } = param;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "h6",
                weight: "normal",
                className: styles.title,
                testId: testId,
                children: title
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.cards,
                children: children
            })
        ]
    });
};
export function AvailableAppsPage() {
    const { t: availableAppsTexts } = useLocaleKeys('integrations.apps');
    const title = availableAppsTexts.pageTitle();
    const { t: commonTexts } = useLocaleKeys('common');
    const businessId = useStore(selectors.user.businessId);
    const { integrations, error, isLoading: isLoadingIntegrations, refresh: refreshIntegrations } = useIntegrations({
        enabled: true,
        refreshOnHookMount: true,
        withLoader: true
    });
    const [isConnectingToQbo, setIsConnectingToQbo] = useState(false);
    const isLoading = isLoadingIntegrations || isConnectingToQbo;
    const { connect: connectToQbo } = useIntegration();
    const { browser: { changeLocation } } = useIoContext();
    const karbonIntegration = integrationModel.karbonIntegration(integrations);
    const clientHubIntegration = integrationModel.clientHubIntegration(integrations);
    const financialCentsIntegration = integrationModel.financialCentsIntegration(integrations);
    const keeperIntegration = integrationModel.keeperIntegration(integrations);
    const xeroIntegration = integrationModel.xeroIntegration(integrations);
    const gmailIntegration = integrationModel.gmailIntegration(integrations);
    const outlookIntegration = integrationModel.outlookIntegration(integrations);
    const isStanfordTaxConnected = integrationModel.isLinkedToStanfordTax(integrations);
    const { ABACOR_INTEGRATION, PING_INTEGRATION, ZAPIER_TEMPLATE_INTEGRATIONS, STANFORD_TAX_INTEGRATION } = useFeatureFlags();
    const { showErrorToast } = useOverlayContext();
    const { AvailableAppsPageTID } = useMemo(()=>GetAvailableAppsPageTID(), []);
    useSetDocumentTitle(title);
    useEmailAuthCompletion({
        businessId,
        onSuccess: async ()=>{
            await refreshIntegrations({
                throwOnError: false
            });
        },
        onError: (errorMessage, toastId)=>{
            showErrorToast(errorMessage, toastId);
        },
        onAccessDenied: ()=>{
            showErrorToast(commonTexts.toasts.failedToConnectYourEmailAccount(), 'integrations-access-denied');
        }
    });
    const handleConnectToQbo = async ()=>{
        setIsConnectingToQbo(true);
        const linkUrl = await connectToQbo();
        if (linkUrl) {
            const connectUrl = new URL(linkUrl);
            connectUrl.searchParams.append('host', window.location.host);
            changeLocation(connectUrl.toString());
        } else {
            await refreshIntegrations({
                throwOnError: false
            });
            setIsConnectingToQbo(false);
        }
    };
    return /*#__PURE__*/ _jsxs(Routes, {
        children: [
            /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.qboSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(AccountingSetupPage, {
                    accounting: Accounting.QBO
                })
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.xeroSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(AccountingSetupPage, {
                    accounting: Accounting.Xero
                })
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.slackIntegration, routes.availableApps),
                element: /*#__PURE__*/ _jsx(SlackViaZapierPage, {})
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.hubspotIntegration, routes.availableApps),
                element: /*#__PURE__*/ _jsx(HubspotViaZapierPage, {})
            }),
            ZAPIER_TEMPLATE_INTEGRATIONS && /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.zapierTemplatesIntegration(), routes.availableApps),
                element: /*#__PURE__*/ _jsx(ZapierTemplatesPage, {})
            }),
            financialCentsIntegration && integrationModel.isLinked(financialCentsIntegration) && /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.financialCentsSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(FinancialCentsSetupPage, {})
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.karbonSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(KarbonSetupPage, {
                    integration: karbonIntegration
                })
            }),
            clientHubIntegration && /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.clientHubSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(ClientHubSetupPage, {
                    integration: clientHubIntegration
                })
            }),
            keeperIntegration && integrationModel.isLinked(keeperIntegration) && /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.keeperSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(KeeperSetupPage, {
                    integration: keeperIntegration
                })
            }),
            STANFORD_TAX_INTEGRATION && isStanfordTaxConnected && /*#__PURE__*/ _jsx(Route, {
                path: relativePath(routes.stanfordTaxSetup, routes.availableApps),
                element: /*#__PURE__*/ _jsx(StanfordTaxSetupPage, {})
            }),
            /*#__PURE__*/ _jsx(Route, {
                path: "*",
                element: error ? /*#__PURE__*/ _jsx(PageErrorState, {
                    testId: AvailableAppsPageTID.ErrorState(),
                    title: title,
                    onRefresh: ()=>refreshIntegrations({
                            throwOnError: false
                        })
                }) : /*#__PURE__*/ _jsxs(Page, {
                    testId: AvailableAppsPageTID.Page(),
                    header: title,
                    subheader: availableAppsTexts.subheader(),
                    children: [
                        isLoading && /*#__PURE__*/ _jsx(FunctionalLoader, {
                            show: isLoading,
                            name: "loadingIntegrations"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.section,
                            children: [
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: availableAppsTexts.sectionTitles.accountingSoftware(),
                                    testId: AvailableAppsPageTID.SectionTitle("accountingSoftware"),
                                    children: [
                                        !!integrations && /*#__PURE__*/ _jsx(QboCard, {
                                            connect: handleConnectToQbo
                                        }),
                                        xeroIntegration && /*#__PURE__*/ _jsx(XeroCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: availableAppsTexts.sectionTitles.communications(),
                                    testId: AvailableAppsPageTID.SectionTitle("communications"),
                                    children: [
                                        gmailIntegration && /*#__PURE__*/ _jsx(GmailCard, {
                                            integration: gmailIntegration,
                                            isDisabled: !!outlookIntegration && integrationModel.isLinked(outlookIntegration)
                                        }),
                                        outlookIntegration && /*#__PURE__*/ _jsx(OutlookCard, {
                                            integration: outlookIntegration,
                                            isDisabled: !!gmailIntegration && integrationModel.isLinked(gmailIntegration)
                                        }),
                                        /*#__PURE__*/ _jsx(SlackViaZapierCard, {}),
                                        /*#__PURE__*/ _jsx(ZapierTemplateIntegrationCards, {
                                            group: "communications"
                                        }),
                                        PING_INTEGRATION && /*#__PURE__*/ _jsx(PingCard, {}),
                                        ABACOR_INTEGRATION && /*#__PURE__*/ _jsx(AbacorCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: availableAppsTexts.sectionTitles.workflowAutomation(),
                                    testId: AvailableAppsPageTID.SectionTitle("workflowAutomation"),
                                    children: [
                                        /*#__PURE__*/ _jsx(MakeCard, {}),
                                        /*#__PURE__*/ _jsx(ZapierCard, {})
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: availableAppsTexts.sectionTitles.practiceManagement(),
                                    testId: AvailableAppsPageTID.SectionTitle("practiceManagement"),
                                    children: [
                                        clientHubIntegration && /*#__PURE__*/ _jsx(ClientHubCard, {
                                            integration: clientHubIntegration
                                        }),
                                        /*#__PURE__*/ _jsx(CountCard, {}),
                                        financialCentsIntegration && /*#__PURE__*/ _jsx(FinancialCentsCard, {
                                            integration: financialCentsIntegration
                                        }),
                                        karbonIntegration && /*#__PURE__*/ _jsx(KarbonCard, {
                                            integration: karbonIntegration
                                        }),
                                        keeperIntegration && /*#__PURE__*/ _jsx(KeeperCard, {
                                            integration: keeperIntegration
                                        }),
                                        /*#__PURE__*/ _jsx(LevvyCard, {}),
                                        /*#__PURE__*/ _jsx(ZapierTemplateIntegrationCards, {
                                            group: "practiceManagement"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(IntegrationSection, {
                                    title: availableAppsTexts.sectionTitles.crm(),
                                    testId: AvailableAppsPageTID.SectionTitle("crm"),
                                    children: [
                                        /*#__PURE__*/ _jsx(HubspotViaZapierCard, {}),
                                        /*#__PURE__*/ _jsx(MondayCard, {}),
                                        /*#__PURE__*/ _jsx(ZapierTemplateIntegrationCards, {
                                            group: "crm"
                                        })
                                    ]
                                }),
                                STANFORD_TAX_INTEGRATION && /*#__PURE__*/ _jsx(IntegrationSection, {
                                    title: availableAppsTexts.sectionTitles.taxWorkflow(),
                                    testId: AvailableAppsPageTID.SectionTitle("taxWorkflow"),
                                    children: /*#__PURE__*/ _jsx(StanfordTaxCard, {
                                        isConnected: isStanfordTaxConnected
                                    })
                                }),
                                ZAPIER_TEMPLATE_INTEGRATIONS && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(IntegrationSection, {
                                            title: availableAppsTexts.sectionTitles.forms(),
                                            testId: AvailableAppsPageTID.SectionTitle("forms"),
                                            children: /*#__PURE__*/ _jsx(ZapierTemplateIntegrationCards, {
                                                group: "forms"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(IntegrationSection, {
                                            title: availableAppsTexts.sectionTitles.scheduling(),
                                            testId: AvailableAppsPageTID.SectionTitle("scheduling"),
                                            children: /*#__PURE__*/ _jsx(ZapierTemplateIntegrationCards, {
                                                group: "scheduling"
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
